// ===========================================
// Parent Permission Slips
// ===========================================
// The portal list: what still needs signing, and what has been answered.

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import EventIcon from '@mui/icons-material/Event';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PageHeader from '../../components/common/PageHeader';
import { useParentPermissionSlips } from '../../hooks/usePermissionSlips';
import { useTenant } from '../../contexts/TenantContext';
import { formatUTCDate } from '../../utils/dateUtils';
import type { PermissionSlipResponse } from '../../api/permissionSlips.api';

// === SLIP CARD ===

interface SlipCardProps {
  response: PermissionSlipResponse;
  onClick: () => void;
}

function SlipCard({ response, onClick }: SlipCardProps) {
  const slip = response.PermissionSlip;
  const isPending = response.status === 'PENDING';

  // Deadline is a @db.Date at UTC midnight — formatUTCDate keeps the day from
  // shifting for a viewer west of UTC.
  const deadline = slip?.deadline ? formatUTCDate(slip.deadline) : null;
  // ⚠️ BOTH SIDES MUST BE UTC. `deadline` is a @db.Date parsed at UTC midnight;
  // comparing it against LOCAL midnight (`new Date().toDateString()`) differs
  // by up to a day west of UTC, so a slip due today reads as "Overdue". This
  // exact UTC/local mismatch has shipped twice in this codebase — and per
  // CLAUDE.md, a test on this Windows box cannot catch it, because TZ is
  // ignored by the runner.
  const startOfTodayUTC = (() => {
    const d = new Date();
    return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  })();
  const overdue =
    isPending && slip?.deadline ? new Date(slip.deadline).getTime() < startOfTodayUTC : false;

  const borderColor = isPending ? (overdue ? '#ff4040' : '#FFCE00') : '#96DAC9';

  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderTop: `4px solid ${borderColor}`,
        boxShadow: 'none',
      }}
    >
      <CardActionArea
        onClick={onClick}
        sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
      >
        <CardContent sx={{ flex: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Chip
              size="small"
              label={
                response.status === 'PENDING'
                  ? overdue
                    ? 'Overdue'
                    : 'Needs signing'
                  : response.status === 'SIGNED'
                    ? 'Signed'
                    : 'Declined'
              }
              color={
                response.status === 'PENDING' ? (overdue ? 'error' : 'warning') : 'success'
              }
            />
            <Typography variant="caption" color="text.secondary">
              {response.Student.firstName}
            </Typography>
          </Stack>

          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            {slip?.title ?? 'Permission slip'}
          </Typography>

          {deadline && (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <EventIcon fontSize="small" sx={{ color: 'text.secondary', flexShrink: 0 }} />
              <Typography variant="body2" color="text.secondary">
                {isPending ? `Respond by ${deadline}` : `Was due ${deadline}`}
              </Typography>
            </Stack>
          )}

          {response.status === 'SIGNED' && response.signedByName && (
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
              <CheckCircleIcon fontSize="small" sx={{ color: '#96DAC9', flexShrink: 0 }} />
              <Typography variant="body2" color="text.secondary">
                Signed by {response.signedByName}
              </Typography>
            </Stack>
          )}
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

// === MAIN PAGE ===

export default function ParentPermissionSlipsPage() {
  const navigate = useNavigate();
  const { schoolSlug } = useTenant();
  const { data: responses = [], isLoading, isError } = useParentPermissionSlips();

  // Only chase slips that were actually sent — a DRAFT or FAILED slip is not
  // something a parent can act on, and showing it would be confusing.
  const live = useMemo(
    () => responses.filter((r) => r.PermissionSlip?.status === 'SENT' || r.status !== 'PENDING'),
    [responses]
  );
  const pending = useMemo(() => live.filter((r) => r.status === 'PENDING'), [live]);
  const answered = useMemo(() => live.filter((r) => r.status !== 'PENDING'), [live]);

  const openSlip = (slipId: string) => {
    navigate(
      schoolSlug ? `/${schoolSlug}/parent/permission-slips/${slipId}` : `permission-slips/${slipId}`
    );
  };

  return (
    <Box>
      <PageHeader
        title="Permission Slips"
        subtitle="Give permission for activities, excursions and camps"
        breadcrumbs={[{ label: 'Dashboard', path: '..' }, { label: 'Permission Slips' }]}
      />

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : isError ? (
        <Alert severity="error">Failed to load permission slips. Please try again.</Alert>
      ) : live.length === 0 ? (
        <Alert severity="info" icon={<FactCheckIcon />}>
          You have no permission slips right now. We&apos;ll email you when one needs signing.
        </Alert>
      ) : (
        <Stack spacing={4}>
          {pending.length > 0 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Needs your response ({pending.length})
              </Typography>
              <Grid container spacing={2}>
                {pending.map((r) => (
                  <Grid item xs={12} sm={6} md={4} key={r.id}>
                    <SlipCard
                      response={r}
                      onClick={() => openSlip(r.PermissionSlip?.id ?? '')}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {answered.length > 0 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Already answered
              </Typography>
              <Grid container spacing={2}>
                {answered.map((r) => (
                  <Grid item xs={12} sm={6} md={4} key={r.id}>
                    <SlipCard
                      response={r}
                      onClick={() => openSlip(r.PermissionSlip?.id ?? '')}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </Stack>
      )}
    </Box>
  );
}
